<script setup lang="ts">
import { ChevronRight } from "lucide-vue-next";

const props = defineProps<{
  title: string;
  subtitle?: string;
  description?: string | null;
  period?: string;
  last?: boolean;
}>();

const emit = defineEmits<{
  details: [];
}>();

const descriptionElement = ref<HTMLElement | null>(null);
const descriptionIsLong = ref(false);
let descriptionResizeObserver: ResizeObserver | null = null;

async function checkDescriptionOverflow() {
  await nextTick();

  const element = descriptionElement.value;

  if (!element || !props.description) {
    descriptionIsLong.value = false;
    return;
  }

  descriptionIsLong.value = element.scrollHeight > element.clientHeight + 1;
}

watch(
  () => props.description,
  async () => {
    descriptionIsLong.value = false;
    await checkDescriptionOverflow();
  },
);

onMounted(async () => {
  await checkDescriptionOverflow();

  if (!descriptionElement.value) return;

  descriptionResizeObserver = new ResizeObserver(() => {
    void checkDescriptionOverflow();
  });

  descriptionResizeObserver.observe(descriptionElement.value);
});

onBeforeUnmount(() => {
  descriptionResizeObserver?.disconnect();
  descriptionResizeObserver = null;
});
</script>

<template>
  <article class="relative min-w-0 pb-6 pl-10 last:pb-0 sm:pl-11">
    <span
      class="absolute left-[11px] top-[7px] z-10 size-2.5 rounded-full bg-primary ring-4 ring-white"
      aria-hidden="true"
    />

    <span
      v-if="!last"
      class="absolute bottom-0 left-[15px] top-[18px] w-px bg-primary/25"
      aria-hidden="true"
    />

    <div class="grid min-w-0 gap-x-5 gap-y-1 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div class="min-w-0">
        <h3 class="break-words text-base font-bold leading-6 text-app-text">
          {{ title }}
        </h3>

        <p
          v-if="subtitle"
          class="mt-0.5 break-words text-sm leading-6 text-app-muted"
        >
          {{ subtitle }}
        </p>

        <time
          v-if="period"
          class="mt-1 block text-sm font-semibold tabular-nums text-primary sm:hidden"
        >
          {{ period }}
        </time>
      </div>

      <time
        v-if="period"
        class="hidden shrink-0 whitespace-nowrap pt-0.5 text-sm font-semibold tabular-nums text-primary sm:block"
      >
        {{ period }}
      </time>
    </div>

    <div v-if="description" class="mt-2.5 min-w-0">
      <p
        ref="descriptionElement"
        class="line-clamp-2 whitespace-pre-wrap break-words text-sm leading-6 text-app-text/75"
      >
        {{ description }}
      </p>

      <button
        v-if="descriptionIsLong"
        type="button"
        class="mt-1.5 inline-flex items-center gap-1 text-sm font-semibold text-primary transition hover:opacity-75"
        @click="emit('details')"
      >
        {{ $t("worker_profile.show_more") }}
        <ChevronRight class="size-4" />
      </button>
    </div>
  </article>
</template>
